
import React, { createContext, useContext, useEffect, useState } from 'react';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { Session, User } from '@supabase/supabase-js';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
        
        // Removed the sign-in success toast
        if (event === 'SIGNED_OUT') {
          toast({
            title: "Signed out",
            description: "You have been successfully signed out."
          });
        }
      }
    );

    // Check for existing session on load
    const initializeAuth = async () => {
      setLoading(true);
      
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();

        if (error) {
          logger.error("Error fetching session:", error);
          return;
        }
        
        if (initialSession) {
          setSession(initialSession);
          setUser(initialSession.user);
        }
      } catch (error) {
        logger.error("Unexpected error during session retrieval:", error);
      } finally {
        setLoading(false);
      }
    };
    
    initializeAuth();

    return () => subscription.unsubscribe();
  }, []);

  const signOutUser = async (): Promise<void> => {
    try {
      logger.log("[AuthContext] Starting sign out process");

      // Clear local state immediately for better UX
      setSession(null);
      setUser(null);

      const { error } = await supabase.auth.signOut();
      if (error) {
        logger.error("Sign out error:", error);
        // Don't show toast error for iOS simulator network issues
        // The local state is already cleared
        logger.log("[AuthContext] Sign out completed with error, but local state cleared");
      } else {
        logger.log("[AuthContext] Sign out completed successfully");
      }
    } catch (error: unknown) {
      logger.error("Unexpected sign out error:", error);
      // Still clear local state even if network request fails
      setSession(null);
      setUser(null);
      logger.log("[AuthContext] Sign out completed with exception, but local state cleared");
    }
  };

  const deleteAccount = async (): Promise<void> => {
    try {
      logger.log("[AuthContext] Starting account deletion process");

      // Call the database function to delete all user data and auth account
      const { error: dbError } = await supabase.rpc('delete_user_account');

      if (dbError) {
        logger.error("Error deleting user account:", dbError);
        throw new Error(dbError.message);
      }

      // Sign out to clear Supabase session storage
      await supabase.auth.signOut();

      // Clear local state
      setSession(null);
      setUser(null);

      logger.log("[AuthContext] Account deletion completed successfully");
    } catch (error: unknown) {
      logger.error("Unexpected account deletion error:", error);
      throw error;
    }
  };

  const value = {
    session,
    user,
    loading,
    signOut: signOutUser,
    deleteAccount
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

